import { Loading } from "modules/shared/components/Loading"
import { useCustomerIssuesQuery } from "modules/support/hooks/useCustomerIssuesQuery"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { IssueCard } from "./IssueCard"

type Props = {
  customerId: string
  selectedIssueId?: string
  onConversationClick: (conversationId: string) => void
  hideEmptyState?: boolean
  createdAtFrom?: string
}

export function CustomerIssues(props: Props) {
  const {
    customerId,
    selectedIssueId,
    onConversationClick,
    createdAtFrom,
    hideEmptyState = false,
  } = props
  const { t } = useTranslation()

  const selectedRef = useRef<HTMLDivElement | null>(null)
  const [openIssueId, setOpenIssueId] = useState(selectedIssueId)
  const { data, isPending } = useCustomerIssuesQuery({
    query: { filterBy: { customerId } },
  })
  const issues = useMemo(
    () =>
      data?.flatMap((issue) => {
        if (!createdAtFrom) return [issue]

        const updates = issue.updates.filter((update) => update.createdAt >= createdAtFrom)
        if (updates.length === 0) return []

        return [
          {
            ...issue,
            updates,
            integrations: issue.integrations.filter(
              (integration) => integration.createdAt >= createdAtFrom,
            ),
            media: issue.media.filter((item) => item.createdAt >= createdAtFrom),
          },
        ]
      }) ?? [],
    [createdAtFrom, data],
  )

  useEffect(() => {
    setOpenIssueId(selectedIssueId)
  }, [customerId, selectedIssueId])

  useEffect(() => {
    if (!isPending && selectedIssueId && selectedRef.current) {
      selectedRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [isPending, issues, selectedIssueId])

  if (isPending) return <Loading />

  if (issues.length === 0 && hideEmptyState) return null

  if (issues.length === 0) {
    return (
      <div className="flex flex-col gap-1 px-4 py-8 items-center justify-center text-center max-w-md mx-auto">
        <span className="text-lg">{t("support.entities.issuesEmpty")}</span>
        <span className="text-sm text-muted-foreground">
          {t("support.entities.issuesEmptyDescription")}
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {issues.map((issue) => {
        const isSelected = selectedIssueId === issue.id

        return (
          <div key={issue.id} ref={isSelected ? selectedRef : undefined}>
            <IssueCard
              issue={issue}
              isOpen={openIssueId === issue.id}
              onOpenChange={(isOpen) => setOpenIssueId(isOpen ? issue.id : undefined)}
              onConversationClick={onConversationClick}
            />
          </div>
        )
      })}
    </div>
  )
}
